// src/belediye/components/notifications/DuyuruModal.js
//
// Duyuru gönderme MODALI (sistem tasarım dili: BasicModal + md-input/md-select/btn-md).
//
// FAZ-77 — HEDEFLEME ARTIK ROLE/SCOPE'A DEĞİL, İZNE BAKAR.
// Eskiden "Genel (tüm personel)" düğmesi `scope.mode === "all_units"` ile
// açılıyordu; yani duyuru gönderme iznini bir müdüre verseniz bile bu düğmeyi
// göremiyor, backend de aynı kilidi koda gömülü uyguluyordu. İki izin var:
//   • bildirim.duyuru.gonder → kapsam içi (izinli birim / o birimdeki rol/kişi)
//   • bildirim.duyuru.genel  → kurum geneli (tüm personel)
// İkisi de Roller ekranından istenen role atanabilir.
// Kategoriler sade + Türkçe: Bilgi / Uyarı / Acil. Opsiyonel zamanlama.

import React, { useEffect, useMemo, useState } from "react";
import BasicModal from "../ui/BasicModal";
import usePermissions from "../../hooks/usePermissions";
import { fetchUnits } from "../../services/belediyeApi";
import { sendDuyuru } from "../../services/bildirimApi";
import { DUYURU_LEVELS } from "./notificationMeta";
import "./DuyuruModal.css";

export default function DuyuruModal({ open, onClose, onSent }) {
  const { permissions } = usePermissions();
  const canBroadcast =
    Array.isArray(permissions) && permissions.includes("bildirim.duyuru.genel");

  const [units, setUnits] = useState([]);
  const [hedef, setHedef] = useState(canBroadcast ? "broadcast" : "unit");
  const [unitId, setUnitId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [level, setLevel] = useState("info");
  const [scheduled, setScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // modal açılınca birimleri getir + formu sıfırla
  useEffect(() => {
    if (!open) return;
    setError("");
    setOkMsg("");
    setHedef(canBroadcast ? "broadcast" : "unit");
    let alive = true;
    fetchUnits()
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : data?.results || [];
        setUnits(list.filter((u) => u && u.id && u.name));
      })
      .catch(() => alive && setUnits([]));
    return () => {
      alive = false;
    };
  }, [open, canBroadcast]);

  const canSubmit = useMemo(() => {
    if (!title.trim() || sending) return false;
    if (hedef === "unit" && !unitId) return false;
    return true;
  }, [title, sending, hedef, unitId]);

  const reset = () => {
    setTitle("");
    setBody("");
    setLevel("info");
    setScheduled(false);
    setScheduledFor("");
    setUnitId("");
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSending(true);
    setError("");
    setOkMsg("");
    try {
      const res = await sendDuyuru({
        type: hedef === "broadcast" ? "broadcast" : "unit",
        unitId: hedef === "unit" ? Number(unitId) : undefined,
        title: title.trim(),
        body: body.trim(),
        level,
        scheduledFor: scheduled && scheduledFor ? new Date(scheduledFor).toISOString() : undefined,
      });
      setOkMsg(
        `Duyuru ${res?.scheduled ? "zamanlandı" : "gönderildi"} — ${res?.created ?? 0} alıcı.`
      );
      reset();
      if (typeof onSent === "function") onSent(res);
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.detail ||
        (data && typeof data === "object" && Object.values(data)?.[0]) ||
        err?.message ||
        "Duyuru gönderilemedi.";
      setError(String(Array.isArray(msg) ? msg[0] : msg));
    } finally {
      setSending(false);
    }
  };

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title="Duyuru Gönder"
      size="md"
      actions={
        <>
          <button type="button" className="btn-md btn-md-ghost" onClick={onClose}>
            Kapat
          </button>
          <button type="button" className="btn-md btn-md-primary" disabled={!canSubmit} onClick={submit}>
            {sending ? "Gönderiliyor…" : scheduled ? "Zamanla" : "Gönder"}
          </button>
        </>
      }
    >
      <div className="dm-body">
        {okMsg ? <div className="dm-alert dm-alert--ok">{okMsg}</div> : null}
        {error ? <div className="dm-alert dm-alert--err">{error}</div> : null}

        {/* Hedef */}
        <div className="dm-field">
          <span className="dm-label">Kime</span>
          <div className="dm-seg">
            {canBroadcast ? (
              <button
                type="button"
                className={`dm-seg-btn ${hedef === "broadcast" ? "on" : ""}`}
                onClick={() => setHedef("broadcast")}
              >
                Genel (tüm personel)
              </button>
            ) : null}
            <button
              type="button"
              className={`dm-seg-btn ${hedef === "unit" ? "on" : ""}`}
              onClick={() => setHedef("unit")}
            >
              Belirli birim
            </button>
          </div>
          {!canBroadcast ? (
            <span className="dm-hint">
              Yetkiniz yalnız birim bazlı duyuruya izin veriyor. Kurum geneli
              duyuru için “Kurum geneli duyuru gönder” izni gerekir.
            </span>
          ) : null}
        </div>

        {hedef === "unit" ? (
          <label className="dm-field">
            <span className="dm-label">Birim *</span>
            <select className="md-select" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">Birim seçin…</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="dm-field">
          <span className="dm-label">Başlık *</span>
          <input
            className="md-input"
            value={title}
            maxLength={160}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Örn: Bu akşam 22:00 sistem bakımı"
          />
        </label>

        <label className="dm-field">
          <span className="dm-label">Mesaj</span>
          <textarea
            className="md-input dm-textarea"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Detay (opsiyonel)"
          />
        </label>

        <label className="dm-field">
          <span className="dm-label">Kategori</span>
          <select className="md-select" value={level} onChange={(e) => setLevel(e.target.value)}>
            {DUYURU_LEVELS.map((l) => (
              <option key={l.key} value={l.key}>
                {l.label}
              </option>
            ))}
          </select>
        </label>

        <label className="dm-check">
          <input type="checkbox" checked={scheduled} onChange={(e) => setScheduled(e.target.checked)} />
          <span>İleri bir tarihe zamanla</span>
        </label>
        {scheduled ? (
          <label className="dm-field">
            <span className="dm-label">Yayın zamanı</span>
            <input
              className="md-input"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
            />
          </label>
        ) : null}
      </div>
    </BasicModal>
  );
}
